import React, { useEffect, useState, useCallback } from "react";
import { BookOpen, ArrowDownCircle, ArrowUpCircle, RefreshCw, Loader2, Download, Filter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Calendar, X } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/DashLayout";
import SearchableSelect from "@/components/ui/SearchableSelect";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_LABELS = { credit: "Credit", debit: "Debit", refund: "Refund" };
const REF_LABELS = { top_up: "Top-up", recharge: "Recharge", refund: "Refund", manual_adjustment: "Adjustment" };
const PAGE_SIZES = [25, 50, 100];

export default function WalletLedger() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25, dealers: [] });
  const [loading, setLoading] = useState(true);
  const [dealerFilter, setDealerFilter] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const total = data.total;
  const pageCount = Math.max(1, Math.ceil(total / perPage));

  // ── Server-side fetch — triggered by page, perPage, or dealer filter ──
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("limit", perPage);
      if (dealerFilter) params.set("dealer_id", dealerFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data: res } = await api.get(`/admin/wallet/ledger?${params.toString()}`);
      setData(res);
    } catch { }
    setLoading(false);
  }, [page, perPage, dealerFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Clamp page when filters/perPage change
  useEffect(() => {
    if (page > pageCount && pageCount > 0) setPage(pageCount);
  }, [pageCount, page]);

  // ── Page summary (current page only) ──
  const totalCredits = data.items.reduce((s, r) => s + (r.credit || 0), 0);
  const totalDebits = data.items.reduce((s, r) => s + (r.debit || 0), 0);

  const startRow = total > 0 ? (page - 1) * perPage + 1 : 0;
  const endRow = Math.min(page * perPage, total);

  // ── CSV Export ──
  const handleExportCSV = () => {
    if (!data.items.length) return;
    const headers = ["#", "Date", "Dealer", "Mobile", "Type", "Reference", "Opening (₹)", "Credit (₹)", "Debit (₹)", "Closing (₹)", "Note"];
    const rows = data.items.map((r, i) => [
      r.seq_no || i + 1,
      r.date ? new Date(r.date).toLocaleString("en-IN") : "",
      r.dealer_name, r.dealer_mobile,
      TYPE_LABELS[r.type] || r.type,
      REF_LABELS[r.reference_type] || r.reference_type || "",
      r.opening_balance.toFixed(2),
      r.credit > 0 ? r.credit.toFixed(2) : "",
      r.debit > 0 ? r.debit.toFixed(2) : "",
      r.closing_balance.toFixed(2),
      `"${(r.note || "").replace(/"/g, '""')}"`,
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `wallet_ledger_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Pagination page numbers (max 5 visible) ──
  const visiblePages = (() => {
    const arr = [];
    let lo = Math.max(1, page - 2);
    let hi = Math.min(pageCount, page + 2);
    if (hi - lo < 4) {
      if (lo === 1) hi = Math.min(pageCount, lo + 4);
      else lo = Math.max(1, hi - 4);
    }
    for (let i = lo; i <= hi; i++) arr.push(i);
    return arr;
  })();

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-brand-primary" />
            Wallet Ledger Report
          </h3>
          <p className="text-sm text-slate-400 mt-0.5">Transaction-wise opening & closing balances for all dealers</p>
        </div>
        <button onClick={handleExportCSV} disabled={!data.items.length}
          className="inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-white font-semibold px-4 py-2 rounded-md disabled:opacity-40 transition-colors">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Total Entries</p>
          <p className="font-heading font-bold text-2xl text-brand-nav mt-1">{total}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[10px] text-green-500 uppercase font-bold tracking-wider">Credits (this page)</p>
          <p className="font-heading font-bold text-2xl text-green-600 mt-1">{fmt(totalCredits)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[10px] text-red-500 uppercase font-bold tracking-wider">Debits (this page)</p>
          <p className="font-heading font-bold text-2xl text-red-600 mt-1">{fmt(totalDebits)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Net (this page)</p>
          <p className={`font-heading font-bold text-2xl mt-1 ${totalCredits - totalDebits >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {fmt(totalCredits - totalDebits)}
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-4">
        <Filter className="w-4 h-4 text-slate-400 shrink-0" />

        {/* Dealer filter */}
        <div className="min-w-[180px]">
          <SearchableSelect 
            value={dealerFilter} 
            onChange={val => { setDealerFilter(val); setPage(1); }}
            options={[{ label: "All Dealers", value: "" }, ...(data.dealers || []).map(d => ({ label: d.name, value: d.id }))]}
          />
        </div>

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white"
            title="From date" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white"
            title="To date" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
              className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors" title="Clear dates">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Rows per page */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Rows:</span>
          <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white">
            {PAGE_SIZES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <button onClick={load} className="ml-auto p-2 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/5 rounded-lg transition-colors" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Ledger Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading ledger...
        </div>
      ) : data.items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          No wallet transactions found.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Date & Time</th>
                  <th className="text-left px-4 py-3">Dealer</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Particular</th>
                  <th className="text-right px-4 py-3 whitespace-nowrap bg-blue-50/50">Opening (₹)</th>
                  <th className="text-right px-4 py-3 whitespace-nowrap bg-green-50/50">Credit (₹)</th>
                  <th className="text-right px-4 py-3 whitespace-nowrap bg-red-50/50">Debit (₹)</th>
                  <th className="text-right px-4 py-3 whitespace-nowrap bg-indigo-50/50">Closing (₹)</th>
                  <th className="text-left px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{r.seq_no || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                      {r.date ? (
                        <>
                          <span className="font-medium">{new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                          <span className="text-slate-400 ml-1">{new Date(r.date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-brand-nav text-xs">{r.dealer_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{r.dealer_mobile}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {r.type === "credit" && <ArrowDownCircle className="w-3.5 h-3.5 text-green-500" />}
                        {r.type === "debit" && <ArrowUpCircle className="w-3.5 h-3.5 text-red-500" />}
                        {r.type === "refund" && <RefreshCw className="w-3.5 h-3.5 text-blue-500" />}
                        <Badge tone={r.type === "debit" ? "danger" : r.type === "refund" ? "info" : "success"}>
                          {TYPE_LABELS[r.type] || r.type}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                        {REF_LABELS[r.reference_type] || r.reference_type || "—"}
                      </span>
                    </td>
                    {/* Opening Balance */}
                    <td className="px-4 py-3 text-right font-mono text-xs text-slate-600 bg-blue-50/30 font-medium">
                      {fmt(r.opening_balance)}
                    </td>
                    {/* Credit */}
                    <td className="px-4 py-3 text-right font-mono text-xs bg-green-50/30">
                      {r.credit > 0 ? (
                        <span className="font-bold text-green-600">+{fmt(r.credit)}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    {/* Debit */}
                    <td className="px-4 py-3 text-right font-mono text-xs bg-red-50/30">
                      {r.debit > 0 ? (
                        <span className="font-bold text-red-600">−{fmt(r.debit)}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    {/* Closing Balance */}
                    <td className="px-4 py-3 text-right font-mono text-xs bg-indigo-50/30 font-bold text-brand-nav">
                      {fmt(r.closing_balance)}
                    </td>
                    {/* Note */}
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate" title={r.note || ""}>
                      {r.note || "—"}
                    </td>
                  </tr>
                ))}
                {/* Page totals row */}
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs">
                  <td colSpan={6} className="px-4 py-3 text-right text-slate-500 uppercase tracking-wider">Page Total</td>
                  <td className="px-4 py-3 text-right font-mono text-green-700 bg-green-50/50">
                    {totalCredits > 0 ? `+${fmt(totalCredits)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-red-700 bg-red-50/50">
                    {totalDebits > 0 ? `−${fmt(totalDebits)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-brand-nav bg-indigo-50/50">
                    Net: {fmt(totalCredits - totalDebits)}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Pagination Bar ── */}
          {pageCount > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              {/* Info */}
              <p className="text-xs text-slate-400 order-2 sm:order-1">
                Showing <span className="font-semibold text-slate-600">{startRow}–{endRow}</span> of{" "}
                <span className="font-semibold text-slate-600">{total}</span> entries
                {" "}· Page <span className="font-semibold text-slate-600">{page}</span> of {pageCount}
              </p>

              {/* Controls */}
              <div className="flex items-center gap-1 order-1 sm:order-2">
                <button onClick={() => setPage(1)} disabled={page === 1}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="First page">
                  <ChevronsLeft className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Previous">
                  <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />
                </button>

                {visiblePages[0] > 1 && <span className="text-xs text-slate-400 px-1">…</span>}

                {visiblePages.map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`min-w-[28px] h-7 rounded-md text-xs font-semibold transition-colors ${
                      p === page ? "bg-brand-primary text-white shadow-sm" : "text-slate-500 hover:bg-slate-200"
                    }`}>
                    {p}
                  </button>
                ))}

                {visiblePages[visiblePages.length - 1] < pageCount && <span className="text-xs text-slate-400 px-1">…</span>}

                <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page === pageCount}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Next">
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <button onClick={() => setPage(pageCount)} disabled={page === pageCount}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Last page">
                  <ChevronsRight className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
